import { useContext, useEffect, useState } from 'react';
import { EventEmitterContext } from '@visx/xychart';

/**
 * Custom hook to track cursor position in SVG coordinates.
 * Leverages @visx/xychart's EventEmitter to track pointer position.
 *
 * This is much simpler and more reliable than manual addEventListener:
 * - No need to manage event listener lifecycle
 * - Works correctly with multiple charts on the same page
 * - Handles touch events automatically
 * - Properly accounts for margins and transforms
 *
 * TODO: Can we retrieve this with XYCharts useTooltip? That would help! Unfortunately the coordinates are "screen positioned" and tough to access.
 *
 * @param enabled - Whether cursor tracking is enabled
 * @returns Object containing cursor X/Y positions in SVG space
 */
export function useSvgCursorPosition(enabled: boolean) {
  const [cursorX, setCursorX] = useState<number | null>(null);
  const [cursorY, setCursorY] = useState<number | null>(null);

  // Use visx's EventEmitter which tracks all pointer events
  const eventEmitter = useContext(EventEmitterContext);

  useEffect(() => {
    if (!enabled || !eventEmitter) {
      setCursorX(null);
      setCursorY(null);
      return;
    }

    // Subscribe to pointer move events
    const handlePointerMove = (event?: { svgPoint?: { x: number; y: number } }) => {
      if (event?.svgPoint) {
        setCursorX(event.svgPoint.x);
        setCursorY(event.svgPoint.y);
      }
    };

    // Subscribe to pointer out events
    const handlePointerOut = () => {
      setCursorX(null);
      setCursorY(null);
    };

    // Listen to pointer events from visx
    eventEmitter.on('pointermove', handlePointerMove);
    eventEmitter.on('pointerout', handlePointerOut);

    return () => {
      eventEmitter.off('pointermove', handlePointerMove);
      eventEmitter.off('pointerout', handlePointerOut);
    };
  }, [enabled, eventEmitter]);

  return { cursorX, cursorY };
}
